import { useEffect, useMemo } from 'react';
import { Controller, useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
} from '@mui/material';
import { MdTranslate } from 'react-icons/md';
import { useActiveLocales } from '@features/locales/hooks';
import type { Locale } from '@features/locales/schema';
import { ImageUploadField } from '@features/newsArticles/components/cells/ImageUploadField';
import {
  cropFormSchema,
  type Crop,
  type CropDetail,
  type CropFormTranslation,
  type CropFormValues,
} from '../schema';
import { useCreateCrop, useCrop, useUpdateCrop } from '../hooks';

interface Props {
  open: boolean;
  crop: Crop | null;
  onClose: () => void;
}

function buildFormTranslations(
  locales: Locale[],
  existing: CropDetail['translations'] | undefined,
): CropFormTranslation[] {
  const byCode = new Map(existing?.map((t) => [t.localeCode, t]) ?? []);
  return locales
    .filter((l) => !l.isDefault)
    .map((locale) => ({
      localeCode: locale.code,
      localeName: locale.name,
      name: byCode.get(locale.code)?.name ?? '',
    }));
}

function buildEmptyDefaults(
  locales: Locale[],
  existing?: CropDetail | null,
): CropFormValues {
  return {
    code: existing?.code ?? '',
    name: existing?.name ?? '',
    category: existing?.category ?? null,
    imageStorageKey: existing?.imageStorageKey ?? null,
    thumbnailStorageKey: existing?.thumbnailStorageKey ?? null,
    displayOrder: existing?.displayOrder ?? null,
    isPreselected: existing?.isPreselected ?? false,
    isActive: existing?.isActive ?? true,
    translations: buildFormTranslations(locales, existing?.translations),
  };
}

export function CropFormDialog({ open, crop, onClose }: Props) {
  const isEditing = !!crop;
  const createMutation = useCreateCrop();
  const updateMutation = useUpdateCrop();
  const submitting = createMutation.isPending || updateMutation.isPending;

  const { data: locales = [], isLoading: localesLoading } = useActiveLocales();
  const detailQuery = useCrop(crop?.id ?? null);
  const detail = detailQuery.data ?? null;

  const dataReady = !localesLoading && (!isEditing || detailQuery.isSuccess);

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CropFormValues>({
    resolver: zodResolver(cropFormSchema) as Resolver<CropFormValues>,
    defaultValues: useMemo(() => buildEmptyDefaults([]), []),
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
  });

  useEffect(() => {
    if (!open || !dataReady) return;
    reset(buildEmptyDefaults(locales, isEditing ? detail : null));
  }, [open, dataReady, locales, detail, isEditing, reset]);

  const translations = watch('translations');

  const onSubmit = handleSubmit(async (values) => {
    if (isEditing && crop) {
      await updateMutation.mutateAsync({ id: crop.id, values });
    } else {
      await createMutation.mutateAsync(values);
    }
    onClose();
  });

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditing ? 'Edit crop' : 'New crop'}</DialogTitle>
      <DialogContent dividers>
        {!dataReady ? (
          <Box className="flex items-center justify-center py-12">
            <CircularProgress size={28} />
          </Box>
        ) : (
          <form id="crop-form" onSubmit={onSubmit} className="grid gap-4 pt-2">
            <TextField
              label="Code"
              size="small"
              fullWidth
              disabled={isEditing}
              error={!!errors.code}
              helperText={
                errors.code?.message ??
                (isEditing
                  ? 'Code is immutable post-create.'
                  : 'Lowercase alphanumerics joined by underscores (e.g. cotton, dry_chilli).')
              }
              {...register('code')}
            />

            <TextField
              label="Name"
              size="small"
              fullWidth
              error={!!errors.name}
              helperText={errors.name?.message ?? 'Base (default-locale) display name.'}
              {...register('name')}
            />

            <TextField
              label="Category"
              size="small"
              fullWidth
              error={!!errors.category}
              helperText={errors.category?.message ?? 'Optional · free text (e.g. fibre, cereal, pulse).'}
              {...register('category', {
                setValueAs: (v: string) => (v === '' ? null : v),
              })}
            />

            <Controller
              control={control}
              name="imageStorageKey"
              render={({ field }) => (
                <div>
                  <label className="mb-1.5 block text-[12.5px] font-medium text-foreground">
                    Image
                  </label>
                  <ImageUploadField
                    folder="crops"
                    imageStorageKey={field.value}
                    thumbnailStorageKey={watch('thumbnailStorageKey')}
                    onChange={(next) => {
                      setValue('imageStorageKey', next.imageStorageKey, {
                        shouldValidate: false,
                        shouldDirty: true,
                      });
                      setValue(
                        'thumbnailStorageKey',
                        next.thumbnailStorageKey,
                        { shouldValidate: false, shouldDirty: true },
                      );
                    }}
                    error={errors.imageStorageKey?.message}
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name="displayOrder"
              render={({ field }) => (
                <TextField
                  label="Display order"
                  type="number"
                  size="small"
                  fullWidth
                  error={!!errors.displayOrder}
                  helperText={
                    errors.displayOrder?.message ??
                    'Lower number = earlier in the mobile crop catalog. Leave blank for alphabetical fallback. Negatives allowed (insert at top without renumbering).'
                  }
                  value={field.value ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    field.onChange(v === '' ? null : Number(v));
                  }}
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                />
              )}
            />

            <Controller
              control={control}
              name="isPreselected"
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label={
                    <span className="text-[13px] text-foreground">
                      {field.value
                        ? 'Preselected — checked by default on the mobile onboarding screen for new users'
                        : 'Not preselected — new users start with this crop unchecked'}
                    </span>
                  }
                />
              )}
            />

            <Controller
              control={control}
              name="isActive"
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label={
                    <span className="text-[13px] text-foreground">
                      {field.value
                        ? 'Active — appears in pickers and consumer surfaces'
                        : 'Inactive — hidden from new attachments; existing references preserved'}
                    </span>
                  }
                />
              )}
            />

            {translations.length > 0 && (
              <div className="grid gap-3">
                <div className="-mb-1 mt-2 flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider text-foreground-muted">
                  <MdTranslate size={14} />
                  Translations <span className="font-normal normal-case">· optional</span>
                </div>
                {translations.map((t, i) => {
                  const nameErr = errors.translations?.[i]?.name?.message;
                  return (
                    <TextField
                      key={t.localeCode}
                      label={`Name (${t.localeName})`}
                      size="small"
                      fullWidth
                      error={!!nameErr}
                      helperText={nameErr ?? ' '}
                      {...register(`translations.${i}.name` as const)}
                    />
                  );
                })}
              </div>
            )}
          </form>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="crop-form"
          variant="contained"
          disabled={submitting || !dataReady}
        >
          {submitting ? 'Saving…' : isEditing ? 'Save changes' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
